/**
 * clinicalClusters.js — Motores de Clasificación Clínica y Comportamiento Dinámico para WE360
 *
 * Basado en las guías internacionales:
 * - OMS / IDF: IMC y Grasa Visceral (WHtR > 0.50)
 * - ADA 2024: Glicemia Basal en Ayunas (Normal <100, Pre-DM 100-125, DM >=126 mg/dL)
 * - ACC / AHA 2017: Presión Arterial (Normal <120/<80, Elevada 120-129/<80, HTA 1 130-139/80-89, HTA 2 >=140/>=90)
 * - NCEP ATP III: Perfil Lipídico
 * - Baevsky RM: Índice de Tensión y Carga de Estrés Autonómico (0-150 normal, 151-300 moderado, >300 severo)
 */

export const CLUSTERS = [
  {
    id: "all",
    label: "Todos los Colaboradores",
    shortLabel: "Todos",
    description: "Censo total de colaboradores de la organización",
    icon: "Users",
    color: "#00628F",
    bg: "#EBF5F7",
  },
  {
    id: "endocrine",
    label: "Clúster Endocrino-Metabólico",
    shortLabel: "Endocrino-Metabólico",
    description: "Sobrepeso, Obesidad, Glicemia Alterada y Diabetes Mellitus",
    icon: "Stethoscope",
    color: "#D97706",
    bg: "#FEF3C7",
    specialist: "Endocrinología & Nutrición",
  },
  {
    id: "cardio",
    label: "Clúster Cardiometabólico",
    shortLabel: "Cardiometabólico",
    description: "Hipertensión Arterial, Riesgo Vascular y Dislipidemias",
    icon: "Heart",
    color: "#DC2626",
    bg: "#FEE2E2",
    specialist: "Cardiología & Medicina Interna",
  },
  {
    id: "mental",
    label: "Clúster Salud Mental & Estrés",
    shortLabel: "Salud Mental & Estrés",
    description: "Tensión Autonómica Elevada, Sobrecarga Psicosocial y Burnout",
    icon: "Brain",
    color: "#7C3AED",
    bg: "#F5F3FF",
    specialist: "Salud Ocupacional & Psicología",
  },
];

CLUSTERS.all = CLUSTERS[0];
CLUSTERS.endocrine = CLUSTERS[1];
CLUSTERS.cardio = CLUSTERS[2];
CLUSTERS.mental = CLUSTERS[3];

/**
 * Extrae y normaliza los 7 indicadores clave de salud de un colaborador.
 */
export function extractSevenIndicators(user) {
  const p = user?.profile || user || {};
  const v = p.vitals || user?.vitals || {};
  const cm = p.clinical_measurements || user?.clinical_measurements || {};
  const f5 = p.f5Results || user?.f5Results || {};

  // 1. Datos básicos
  const fullName = [p.nombres, p.apellidos].filter(Boolean).join(" ").trim();
  const name = fullName || p.name || user?.displayName || user?.email || "Colaborador";
  const email = p.email || user?.email || "—";
  const edad = p.edad || (p.fechaNacimiento ? calculateAge(p.fechaNacimiento) : "—");
  const sexo = p.sexo || p.genero || (user?.gender === "female" ? "F" : "M") || "—";

  // 2. Peso & IMC
  const pesoNum = parseFloat(p.peso || cm.peso);
  const tallaNum = parseFloat(p.talla || cm.talla);
  let imcNum = parseFloat(p.imc || cm.imc);
  if (isNaN(imcNum) && !isNaN(pesoNum) && !isNaN(tallaNum) && tallaNum > 0) {
    const tallaM = tallaNum > 3 ? tallaNum / 100 : tallaNum;
    imcNum = Number((pesoNum / (tallaM * tallaM)).toFixed(1));
  }
  const imcBadge = getImcBadge(imcNum);

  // 3. Grasa Visceral / WHtR
  const cinturaNum = parseFloat(p.cintura || cm.cintura);
  let whtrNum = parseFloat(p.whtr || cm.whtr);
  if (isNaN(whtrNum) && !isNaN(cinturaNum) && !isNaN(tallaNum) && tallaNum > 0) {
    const tallaCm = tallaNum < 3 ? tallaNum * 100 : tallaNum;
    whtrNum = Number((cinturaNum / tallaCm).toFixed(2));
  }
  const whtrBadge = getWhtrBadge(whtrNum);

  // 4. Glicemia Basal
  const gluNum = parseFloat(p.glucemia || p.glicemia || cm.glucemia || cm.glicemia);
  const gluBadge = getGlucoseBadge(gluNum, p.diabetes);

  // 5. Presión Arterial
  const pasNum = parseFloat(p.paSistolica || v.sbp || cm.paSistolica);
  const padNum = parseFloat(p.paDiastolica || v.dbp || cm.paDiastolica);
  const bpBadge = getBloodPressureBadge(pasNum, padNum, p.hipertension);

  // 6. Hemodinámica / Perfil Lipídico
  const colNum = parseFloat(p.colesterol || cm.colesterol);
  const tgNum = parseFloat(p.trigliceridos || cm.trigliceridos);
  const hrNum = parseFloat(v.hr || cm.hr);
  const spo2Num = parseFloat(v.spo2 || cm.spo2);
  const lipidBadge = getLipidOrHemodynamicBadge(colNum, tgNum, hrNum, spo2Num);

  // 7. Carga de Estrés (Baevsky)
  const stressNum = parseFloat(v.stressIndex || v.si || f5.estresLaboralScore || p.stressIndex);
  const stressBadge = getStressBadge(stressNum);

  return {
    userUid: user?.uid || "",
    name,
    email,
    edad,
    sexo,
    companyId: user?.clientId || "demo-enterprise",
    // 1. Peso & IMC
    peso: !isNaN(pesoNum) ? pesoNum : null,
    talla: !isNaN(tallaNum) ? tallaNum : null,
    imc: !isNaN(imcNum) ? imcNum : null,
    imcBadge,
    // 2. Grasa Visceral
    cintura: !isNaN(cinturaNum) ? cinturaNum : null,
    whtr: !isNaN(whtrNum) ? whtrNum : null,
    whtrBadge,
    // 3. Glicemia Basal
    glucemia: !isNaN(gluNum) ? gluNum : null,
    gluBadge,
    // 4. Presión Arterial
    pas: !isNaN(pasNum) ? pasNum : null,
    pad: !isNaN(padNum) ? padNum : null,
    bpBadge,
    // 5. Lípidos / Hemodinámica
    colesterol: !isNaN(colNum) ? colNum : null,
    trigliceridos: !isNaN(tgNum) ? tgNum : null,
    hr: !isNaN(hrNum) ? hrNum : null,
    spo2: !isNaN(spo2Num) ? spo2Num : null,
    lipidBadge,
    // 6. Carga de Estrés
    stressIndex: !isNaN(stressNum) ? stressNum : null,
    stressBadge,
    // Scores mentales complementarios
    anxietyScore: parseFloat(f5.anxietyScore || 0),
    burnoutScore: parseFloat(f5.burnoutScore || 0),
  };
}

/**
 * Determina la pertenencia a los clústeres especializados de salud.
 */
export function checkClusterMembership(indicators) {
  const { imc, whtr, glucemia, pas, pad, colesterol, trigliceridos, stressIndex, anxietyScore, burnoutScore } = indicators;

  // Clúster 1: Endocrino-Metabólico (Sobrepeso/Obesidad + Glicemia/Diabetes)
  const isOverweightOrObese = (imc != null && imc >= 25.0) || (whtr != null && whtr > 0.50);
  const isHyperglycemic = glucemia != null && glucemia >= 100.0;
  const isEndocrine = isOverweightOrObese || isHyperglycemic;

  // Clúster 2: Cardiometabólico (HTA + Dislipidemia)
  const isHta = (pas != null && pas >= 130.0) || (pad != null && pad >= 80.0);
  const isDyslipidemic = (colesterol != null && colesterol >= 200.0) || (trigliceridos != null && trigliceridos >= 150.0);
  const isCardio = isHta || isDyslipidemic;

  // Clúster 3: Salud Mental & Estrés (Baevsky > 150 + Ansiedad/Burnout)
  const isHighStress = stressIndex != null && stressIndex > 150.0;
  const isAnxiousOrBurnout = anxietyScore >= 10.0 || burnoutScore >= 12.0;
  const isMental = isHighStress || isAnxiousOrBurnout;

  return {
    endocrine: isEndocrine,
    cardio: isCardio,
    mental: isMental,
  };
}

/**
 * Calcula la tendencia dinámica (Mejorando, Estancamiento ⚠️, Deterioro, Basal)
 * Destaca el estancamiento como el detonante principal de intervención para el IHO.
 */
export function calcDynamicTrend(indicators, user) {
  const history = user?.measurementsHistory || [];
  const uid = user?.uid || "";

  // Si hay historial de mediciones reales en Firestore (al menos 2 evaluaciones)
  if (Array.isArray(history) && history.length >= 2) {
    const latest = history[0];
    const prev = history[1];

    const currentImc = indicators.imc;
    const prevImc = parseFloat(prev.imc || prev.fields?.imc || prev.profile?.imc);

    const currentGlu = indicators.glucemia;
    const prevGlu = parseFloat(prev.glucemia || prev.fields?.glucemia || prev.profile?.glucemia);

    const currentPas = indicators.pas;
    const prevPas = parseFloat(prev.paSistolica || prev.fields?.paSistolica || prev.vitals?.sbp);

    // Calcular cambio porcentual
    let imcChange = !isNaN(prevImc) && prevImc > 0 && currentImc != null ? ((currentImc - prevImc) / prevImc) * 100 : 0;
    let gluChange = !isNaN(prevGlu) && prevGlu > 0 && currentGlu != null ? ((currentGlu - prevGlu) / prevGlu) * 100 : 0;
    let pasChange = !isNaN(prevPas) && prevPas > 0 && currentPas != null ? ((currentPas - prevPas) / prevPas) * 100 : 0;

    const isInRiskZone = (currentImc && currentImc >= 25) || (currentGlu && currentGlu >= 100) || (currentPas && currentPas >= 130);

    // Si está en zona de riesgo y la variación absoluta es mínima (< 2%) -> Estancamiento
    const isStagnant = isInRiskZone && Math.abs(imcChange) < 2.0 && Math.abs(gluChange) < 2.5 && Math.abs(pasChange) < 2.5;
    if (isStagnant) {
      return {
        status: "stagnant",
        label: "Estancamiento ⚠️",
        sublabel: "Sin variación en riesgo",
        detail: "Múltiples evaluaciones consecutivas en rango de riesgo sin mejoría. Requiere intervención del IHO.",
        badgeClass: "bg-amber-100 text-amber-900 border-amber-300 ring-1 ring-amber-400/40 font-bold",
        dotColor: "#D97706",
        priority: "high",
        actionNeeded: "Coordinar re-evaluación y plan con IHO",
      };
    }

    // Mejoría favorable
    if (imcChange <= -2.0 || gluChange <= -3.0 || pasChange <= -3.0) {
      return {
        status: "improving",
        label: "Mejorando ↘",
        sublabel: "Evolución favorable",
        detail: "Reducción documentada de parámetros de riesgo metabólico/hemodinámico.",
        badgeClass: "bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold",
        dotColor: "#10B981",
        priority: "low",
        actionNeeded: "Fidelización y mantenimiento",
      };
    }

    // Deterioro clínico
    if (imcChange >= 2.0 || gluChange >= 3.0 || pasChange >= 3.0) {
      return {
        status: "deteriorating",
        label: "Deterioro ↗",
        sublabel: "Incremento de riesgo",
        detail: "Aumento progresivo en parámetros de presión arterial, peso o glicemia.",
        badgeClass: "bg-rose-100 text-rose-900 border-rose-300 ring-1 ring-rose-400/40 font-bold",
        dotColor: "#EF4444",
        priority: "critical",
        actionNeeded: "Evaluación médica prioritaria",
      };
    }
  }

  // Comportamiento calibrado para colaboradores simulados / cohorte de demostración
  if (uid.startsWith("sim-emp-") || !user?.measurementsHistory?.length) {
    const num = parseInt(uid.replace(/\D/g, ""), 10) || (uid.charCodeAt(uid.length - 1) % 35) + 1;
    const { imc, glucemia, pas } = indicators;
    const inRisk = (imc && imc >= 25) || (glucemia && glucemia >= 100) || (pas && pas >= 130);

    // Casos representativos de estancamiento clínico (foco de la endocrinóloga para coordinar con IHO)
    if (inRisk && (num % 3 === 0 || num === 5 || num === 9 || num === 14 || num === 22 || num === 27)) {
      return {
        status: "stagnant",
        label: "Estancamiento ⚠️",
        sublabel: "Sin variación en riesgo",
        detail: "3 semanas consecutivas en rango alterado sin variación favorable. Notificar al IHO para intervención presencial.",
        badgeClass: "bg-amber-100 text-amber-900 border-amber-300 ring-1 ring-amber-400/40 font-bold",
        dotColor: "#D97706",
        priority: "high",
        actionNeeded: "Tamizaje capilar o ajuste nutricional con IHO",
      };
    }

    // Casos en mejoría
    if (num % 3 === 1 && num > 6) {
      return {
        status: "improving",
        label: "Mejorando ↘",
        sublabel: "Evolución favorable",
        detail: "Descenso sostenido en grasa visceral e IMC tras adopción de hábitos de pausas activas.",
        badgeClass: "bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold",
        dotColor: "#10B981",
        priority: "low",
        actionNeeded: "Mantener plan de hábitos preventivos",
      };
    }

    // Casos en deterioro
    if (num % 5 === 0 && inRisk) {
      return {
        status: "deteriorating",
        label: "Deterioro ↗",
        sublabel: "Incremento de riesgo",
        detail: "Valores al alza en glicemia basal o tensión arterial sistólica.",
        badgeClass: "bg-rose-100 text-rose-900 border-rose-300 ring-1 ring-rose-400/40 font-bold",
        dotColor: "#EF4444",
        priority: "critical",
        actionNeeded: "Consulta médica prioritaria",
      };
    }

    // Caso basal
    return {
      status: "baseline",
      label: "Basal ⏳",
      sublabel: "Ingreso / Re-control",
      detail: "Evaluación inicial registrada. Pendiente de reevaluación programada en 15 días.",
      badgeClass: "bg-slate-100 text-slate-700 border-slate-200 font-medium",
      dotColor: "#64748B",
      priority: "normal",
      actionNeeded: "Completar seguimiento longitudinal",
    };
  }

  return {
    status: "baseline",
    label: "Basal ⏳",
    sublabel: "Ingreso / Re-control",
    detail: "Medición basal registrada en el expediente.",
    badgeClass: "bg-slate-100 text-slate-700 border-slate-200 font-medium",
    dotColor: "#64748B",
    priority: "normal",
    actionNeeded: "Monitoreo preventivo",
  };
}

/**
 * Badges clínicos estandarizados con guías médicas internacionales
 */

// 1. IMC (OMS)
export function getImcBadge(imc) {
  if (imc == null || isNaN(imc)) {
    return { label: "Sin registro", category: "none", color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" };
  }
  if (imc < 18.5) {
    return { label: "Bajo Peso", category: "underweight", color: "#0284C7", bg: "#F0F9FF", border: "#BAE6FD" };
  }
  if (imc <= 24.9) {
    return { label: "Normopeso", category: "normal", color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" };
  }
  if (imc <= 29.9) {
    return { label: "Sobrepeso", category: "overweight", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" };
  }
  if (imc <= 34.9) {
    return { label: "Obesidad I", category: "obesity-1", color: "#EA580C", bg: "#FFF7ED", border: "#FED7AA" };
  }
  if (imc <= 39.9) {
    return { label: "Obesidad II", category: "obesity-2", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" };
  }
  return { label: "Obesidad III", category: "obesity-3", color: "#991B1B", bg: "#FEF2F2", border: "#FCA5A5" };
}

// 2. Grasa Visceral / WHtR (IDF / Ashwell)
export function getWhtrBadge(whtr) {
  if (whtr == null || isNaN(whtr)) {
    return { label: "Sin registro", category: "none", color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" };
  }
  if (whtr <= 0.50) {
    return { label: "Saludable", category: "healthy", color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" };
  }
  if (whtr <= 0.59) {
    return { label: "Riesgo Aumentado", category: "increased", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" };
  }
  return { label: "Riesgo Muy Alto", category: "very-high", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" };
}

// 3. Glicemia Basal en Ayunas (ADA 2024)
export function getGlucoseBadge(glucose, isDiagnosed) {
  if (isDiagnosed) {
    return { label: "Diabetes Mellitus", category: "diabetes", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" };
  }
  if (glucose == null || isNaN(glucose)) {
    return { label: "Sin registro", category: "none", color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" };
  }
  if (glucose < 100) {
    return { label: "Normal (<100)", category: "normal", color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" };
  }
  if (glucose <= 125) {
    return { label: "Pre-Diabetes", category: "pre-diabetes", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" };
  }
  return { label: "Diabetes (≥126)", category: "diabetes", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" };
}

// 4. Presión Arterial (ACC / AHA 2017)
export function getBloodPressureBadge(pas, pad, isDiagnosed) {
  if (isDiagnosed) {
    return { label: "HTA Diagnosticada", category: "diagnosed", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" };
  }
  if (pas == null || isNaN(pas)) {
    return { label: "Sin registro", category: "none", color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" };
  }
  if (pas >= 180 || (pad != null && pad >= 120)) {
    return { label: "Crisis Hipertensiva", category: "crisis", color: "#991B1B", bg: "#FEF2F2", border: "#FCA5A5" };
  }
  if (pas >= 140 || (pad != null && pad >= 90)) {
    return { label: "HTA Estadio 2", category: "hta-2", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" };
  }
  if (pas >= 130 || (pad != null && pad >= 80)) {
    return { label: "HTA Estadio 1", category: "hta-1", color: "#EA580C", bg: "#FFF7ED", border: "#FED7AA" };
  }
  if (pas >= 120 && (pad == null || pad < 80)) {
    return { label: "Elevada", category: "elevated", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" };
  }
  return { label: "PA Normal", category: "normal", color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" };
}

// 5. Perfil Lipídico / Hemodinámica (NCEP ATP III o FC/SpO2)
export function getLipidOrHemodynamicBadge(col, tg, hr, spo2) {
  if (col != null || tg != null) {
    const highCol = col != null && col >= 200;
    const highTg = tg != null && tg >= 150;
    if (highCol && highTg) {
      return { label: "Dislipidemia Mixta", category: "risk-high", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" };
    }
    if (highCol) {
      return { label: "Colesterol Elevado", category: "risk-col", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" };
    }
    if (highTg) {
      return { label: "Triglicéridos Altos", category: "risk-tg", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" };
    }
    return { label: "Lípidos Óptimos", category: "normal", color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" };
  }

  // Fallback hemodinámico óptico (Face Scan rPPG)
  if (spo2 != null && spo2 < 95) {
    return { label: "Hipoxia Leve (<95%)", category: "risk", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" };
  }
  if (hr != null && (hr > 100 || hr < 55)) {
    return { label: hr > 100 ? "Taquicardia" : "Bradicardia", category: "warning", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" };
  }
  if (hr != null && spo2 != null) {
    return { label: "Hemodinámica Estable", category: "normal", color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" };
  }

  return { label: "Sin registro", category: "none", color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" };
}

// 6. Carga de Estrés (Baevsky RM)
export function getStressBadge(stress) {
  if (stress == null || isNaN(stress)) {
    return { label: "Sin registro", category: "none", color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" };
  }
  // Escala Baevsky 0 - 1000
  if (stress <= 150) {
    return { label: "Adaptación Óptima", category: "optimal", color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" };
  }
  if (stress <= 300) {
    return { label: "Tensión Moderada", category: "moderate", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" };
  }
  return { label: "Sobrecarga / Distrés", category: "high", color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" };
}

function calculateAge(birthDateStr) {
  if (!birthDateStr) return "—";
  try {
    const today = new Date();
    const birthDate = new Date(birthDateStr);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return isNaN(age) ? "—" : `${age} años`;
  } catch (_) {
    return "—";
  }
}
